import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone, MapPin, Printer } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { COMPANY, SERVICES } from "@/data/site";

const INTERESTS = [
  "General inquiry",
  "Refer a patient",
  "Service partnership (MCO, broker, hospital)",
  "Consulting / management services",
  "Press / media",
  ...SERVICES.map((s) => s.name),
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", subject: "", interest: INTERESTS[0], message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received — we'll follow up within one business day.");
      setForm({ name: "", email: "", phone: "", subject: "", interest: INTERESTS[0], message: "" });
    } catch (err) {
      toast.error("Could not send. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <PageHeader
        overline="Contact 1st Touch"
        title="Let's talk about"
        italic="the care you need."
        description="Whether you're referring a patient, exploring a partnership, or just learning about us — reach out. A member of our team responds within one business day."
      />

      <section className="py-20 md:py-28 bg-teh-bg">
        <div className="teh-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            <ContactCard icon={Phone} label="Call us" value={COMPANY.phone} href={`tel:${COMPANY.phoneRaw}`} />
            <ContactCard icon={Printer} label="Fax" value={COMPANY.fax} />
            <ContactCard icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} />
            <ContactCard
              icon={MapPin}
              label="Mailing address"
              value={`${COMPANY.addressLine1}, ${COMPANY.addressLine2}`}
            />

            <div className="p-7 bg-teh-deep text-white rounded-2xl">
              <p className="text-xs uppercase tracking-[0.22em] text-emerald-300">Hours</p>
              <p className="font-serif text-2xl mt-4">Monday – Friday</p>
              <p className="text-white/70">8:00am – 6:00pm ET</p>
              <p className="mt-5 text-sm text-white/60">
                For after-hours patient care needs, please call the main line and follow the prompts.
              </p>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-8 bg-white border border-teh-navy/10 rounded-2xl p-8 md:p-12 space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Name" required>
                <input data-testid="contact-name" required value={form.name} onChange={update("name")} className={inputCls} />
              </Field>
              <Field label="Email" required>
                <input type="email" data-testid="contact-email" required value={form.email} onChange={update("email")} className={inputCls} />
              </Field>
              <Field label="Phone">
                <input data-testid="contact-phone" value={form.phone} onChange={update("phone")} className={inputCls} />
              </Field>
              <Field label="Reason for contact">
                <select data-testid="contact-interest" value={form.interest} onChange={update("interest")} className={inputCls}>
                  {INTERESTS.map((i) => <option key={i}>{i}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Subject">
              <input data-testid="contact-subject" value={form.subject} onChange={update("subject")} className={inputCls} />
            </Field>
            <Field label="Message" required>
              <textarea data-testid="contact-message" rows={6} required value={form.message} onChange={update("message")} className={inputCls} />
            </Field>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 px-7 py-4 bg-teh-navy text-white text-sm font-medium rounded-full hover:bg-teh-navyDark hover:gap-3 transition disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send message"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function ContactCard({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-start gap-4 p-6 bg-white border border-teh-navy/10 rounded-2xl hover:border-teh-green/40 transition group">
      <div className="w-11 h-11 rounded-xl bg-teh-green/10 flex items-center justify-center text-teh-green">
        <Icon className="w-5 h-5" strokeWidth={1.5} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-teh-muted">{label}</p>
        <p className="mt-1 font-serif text-xl text-teh-deep group-hover:text-teh-navy">
          {value}
        </p>
      </div>
    </div>
  );
  return href ? <a href={href}>{inner}</a> : inner;
}

const inputCls =
  "w-full px-4 py-3 bg-teh-bg border border-teh-navy/15 rounded-lg text-teh-deep placeholder:text-teh-muted/60 focus:outline-none focus:border-teh-green focus:ring-2 focus:ring-teh-green/15 transition";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-teh-deep mb-2">
        {label} {required && <span className="text-teh-green">*</span>}
      </span>
      {children}
    </label>
  );
}
